// Páginas INTERNAS do modelo "Norte" (topo 17): /imoveis, ficha, /condominios, /empreendimento, /empresa, /blog,
// simulador e filial no visual do modelo (Outfit em tudo, serifa Newsreader só no h1 da página, página BRANCA,
// campos RETOS, botões em PÍLULA, cards brancos de raio 8 com sombra curta, a cor mais escura do tenant no lugar
// do preto da referência). Só importa de norte.ts (paleta/vars das 4 peças) — nada aqui é usado fora do gate
// ehModeloNorte: null ⇒ zero diff nos demais sites.
import type { CSSProperties } from 'react';
import { isMobile } from 'react-device-detect';
import { ehModeloNorte, hexParaRgba, paletaNorte, varsNorteInterna } from './norte';
import type { PaletaNorte } from './norte';

export { ehModeloNorte, paletaNorte } from './norte';

/**
 * Vars da RAIZ de uma página interna: as --nt-* da paleta + Outfit na página toda (varsNorteInterna) + os tons
 * "de página" derivados do texto (--nt-sutil/--nt-sutil-forte) e a cápsula escura das fotos (--nt-capsula) que os
 * módulos CSS das internas leem atrás de [data-modelo='norte']. A régua (--nt-lado: coluna de --nt-largura centrada,
 * 24px de mínimo, --nt-lateral no celular) mora no NorteInterna.module.css (.interna). Fora do topo 17 ⇒ null.
 */
export const varsNortePagina = (site: any): CSSProperties | null => {
    const vn = varsNorteInterna(site);
    if (!vn) return null;
    const p = paletaNorte(site);
    return {
        ...vn,
        '--nt-sutil': hexParaRgba(p.texto, 0.06, 'rgba(23, 23, 23, ALPHA)'),
        '--nt-sutil-forte': hexParaRgba(p.texto, 0.12, 'rgba(23, 23, 23, ALPHA)'),
        '--nt-capsula': hexParaRgba(p.profundo, 0.82, 'rgba(0, 0, 0, ALPHA)'),
    } as CSSProperties;
};

/** Cores que o TSX precisa fora do CSS (menus do react-select em portal no <body>, spinner, slider). */
export type CoresInternasNorte = PaletaNorte & { sutil: string; sombra: string };
export const coresInternasNorte = (site: any): CoresInternasNorte | null => {
    if (!ehModeloNorte(site)) return null;
    const p = paletaNorte(site);
    return {
        ...p,
        sutil: hexParaRgba(p.texto, 0.06, 'rgba(23, 23, 23, ALPHA)'),
        sombra: hexParaRgba(p.profundo, 0.22, 'rgba(0, 0, 0, ALPHA)'),
    };
};

/**
 * react-select na língua do modelo: campo RETO de 48px (a caixa de busca da referência: branca, borda clara, sem
 * raio), foco = borda forte. Menu: painel branco reto com sombra alta; opção escolhida = fundo de apoio + 500.
 */
export const estilosSelectNorte = (c: CoresInternasNorte, painel: boolean) => ({
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 48,
        borderRadius: 0,
        background: '#ffffff',
        boxShadow: 'none',
        border: `1px solid ${state.isFocused ? c.bordaForte : c.borda}`,
        padding: '0 4px 0 8px',
        cursor: 'pointer',
        transition: 'border-color .2s ease',
        ':hover': { borderColor: c.bordaForte },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '2px 6px' }),
    placeholder: (base: any) => ({ ...base, color: c.cinza, fontSize: painel ? 15 : 16, fontWeight: 400 }),
    singleValue: (base: any) => ({ ...base, color: c.tinta, fontSize: painel ? 15 : 16, fontWeight: 400 }),
    multiValue: (base: any) => ({ ...base, borderRadius: 9999, background: c.aba }),
    input: (base: any) => ({ ...base, color: c.tinta }),
    container: (base: any) => ({ ...base, color: c.tinta }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        color: c.tinta,
        transition: 'transform .3s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
        ':hover': { color: c.tinta },
    }),
    clearIndicator: (base: any) => ({ ...base, color: c.cinza, ':hover': { color: c.tinta } }),
    menu: (base: any) => ({
        ...base,
        marginTop: 4,
        backgroundColor: '#ffffff',
        color: c.tinta,
        border: `1px solid ${c.borda}`,
        borderRadius: 0,
        overflow: 'hidden',
        boxShadow: `0 12px 28px -8px ${c.sombra}`,
        zIndex: 10000,
    }),
    menuList: (base: any) => ({ ...base, padding: 4 }),
    option: (base: any, state: any) => ({
        ...base,
        borderRadius: 0,
        fontSize: 15,
        fontWeight: state.isSelected ? 500 : 400,
        cursor: 'pointer',
        backgroundColor: state.isSelected ? c.apoio : state.isFocused && !isMobile ? c.apoio : 'transparent',
        color: c.tinta,
        ':active': { backgroundColor: c.aba },
    }),
    noOptionsMessage: (base: any) => ({ ...base, color: c.cinza }),
    loadingMessage: (base: any) => ({ ...base, color: c.cinza }),
});

/** "12 de outubro de 2026" sem Intl (toLocaleDateString depende do ICU → hydration mismatch). */
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const dataPorExtensoNorte = (valor: any): string => {
    const d = valor instanceof Date ? valor : new Date(String(valor || '').replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};
